import BetModel from "../../src/model/BetModel";
import Layout from "../utils/Layout";
import BaseView from "./BaseView";
import Utils from "../utils/Utils";
import {AbstractMoneyField, MoneyFieldConfig } from "./AbstractMoneyField";
import App from "../App";
import { IObjectConfig } from "../baseConfigs/layoutConfigShema";
import GameModel from "../model/GameModel";
import { ITextFieldStyleOptions } from "./components/TextField";
import { Graphics, Sprite, Text, TextMetrics, TextStyle, Texture } from "pixi.js";
import ConfigManager from "../managers/ConfigManager";
import { CurrencyType } from "../managers/contracts/configurations/IBetConfig";
import { ConfigurationTypes } from "../const/ConfigurationTypes";
import BitmapTextField, { IBitmapTextFieldStyle } from "./components/BitmapTextField";
import { UIComponents } from '../ui/UIComponents';
import Configs from "../../../src/vendorConfigs/Configs";
import { isInteger } from 'lodash';

type TextFontSizes = {
    money: number,
    cents: number,
    currency: number
}

export class MoneyField extends AbstractMoneyField {

    private money: number;
    protected moneyText: Text;
    protected centsText: Text;
    protected currencyText: Text;
    private boundsRect: Graphics;
    protected overrideStyle:ITextFieldStyleOptions;
    // private _config: MoneyFieldConfig;
    private symbol: string = "";
    private useCoins: boolean;
    private coinBitmap: BitmapTextField;

    constructor(config?: MoneyFieldConfig) {
        super(config);
        // if (!config) {
        //     this._config = new MoneyFieldConfig();
        // } else {
        //     this._config = config;
        // }

        const betConfiguration = Configs.bet;
        if (config.alternative) {
            this.useCoins = betConfiguration.alternativeCurrencyType == CurrencyType.Coins;
        } else {
            this.useCoins = betConfiguration.primaryCurrencyType == CurrencyType.Coins;
        }

        if (this.useCoins) {
            this.symbol = "";
        } else {
            this.symbol = BetModel.currency.symbol;
        }

        this.createBoundsRect();
        this.createTextfields();
    }

    public clone():AbstractMoneyField {
        let mf = new MoneyField(Utils.deepClone(this._config));
        mf.bounds = Utils.deepClone(this.bounds);
        if(this.overrideStyle) {
            mf.style = this.overrideStyle;
        }
        return mf;
    }

    public createBoundsRect() {
        if (this.boundsRect && this.boundsRect.parent) {
            this.boundsRect.parent.removeChild(this.boundsRect);
            this.boundsRect.destroy();
        }

        this.boundsRect = new Graphics();
        this.boundsRect.beginFill(0xFFFB1B, 0.5);
        this.boundsRect.drawRect(0, 0, this._config.bounds.width, this._config.bounds.height);
        this.boundsRect.endFill();
        if (!App.instance.debugBounds) {
            this.boundsRect.alpha = 0;
        }
        this.addChild(this.boundsRect);
    }

    public changeColor(color: string | number) {
        this.moneyText.style.fill = color;
        this.centsText.style.fill = color;
        this.currencyText.style.fill = color;
    }

    /**
     * Changes the bounds for this moneyfield in case labelSettings at game-config are set or settings are passed as argument.
     *
     * @return {*}  {void}
     * @memberof MoneyField
     */
    public applyLabelSettings(settings?: { scaleX: number, scaleY: number, offsetX: number, offsetY: number}): void {
        if (!App.config.labelSettings && !settings) {
            return;
        }

        let labelSettings = {
            "scaleX": App.config.labelSettings?.scaleX || 0.8,
            "scaleY": App.config.labelSettings?.scaleY || 0.8,
            "offsetX": App.config.labelSettings?.offsetX || 0,
            "offsetY": App.config.labelSettings?.offsetY || 0
        };

        if (settings) {
            labelSettings.scaleX = settings.scaleX;
            labelSettings.scaleY = settings.scaleY;
            labelSettings.offsetX = settings.offsetX;
            labelSettings.offsetY = settings.offsetY;
        }

        const originalWidth = this.bounds.width;
        const originalHeight = this.bounds.height;

        this.bounds = {
            width: this.bounds.width * labelSettings.scaleX,
            height: this.bounds.height * labelSettings.scaleY
        };

        this.x += Layout.scalePixels((originalWidth - this.bounds.width) / 2 + labelSettings.offsetX);
        this.y += Layout.scalePixels((originalHeight - this.bounds.height) / 2 + labelSettings.offsetY);
    }

    protected createTextfields() {
        if (this.useCoins) {
            const scale = (this._config.coinsRelativeSize / 100) / Layout.globalScale;
            let bitmapStyle: IBitmapTextFieldStyle  = {
                fontName: "coin",
                fontSize: this._config.bounds.height * scale,
                align: "left"
            }

            let bitmapBound: IObjectConfig = {
                x: 0,
                y: 0,
                width: this._config.bounds.width * scale,
                height: this._config.bounds.height * scale
            };

            let bitmapDefinition = {
                bound: bitmapBound ,
                anchor: { x: 0.0, y: 0.5 }
            };
            try {
                this.coinBitmap = new BitmapTextField("$", bitmapStyle, bitmapDefinition);
                this.addChild(this.coinBitmap);
            } catch (error) {
                this.coinBitmap = undefined;
            }
        }

        this.moneyText = new Text("11", {
            fontFamily: this._config.fontFamily,
            fontSize: this._config.moneyFontSize
        });
        this.addChild(this.moneyText);

        this.centsText = new Text("45", {
            fontFamily: this._config.fontFamily,
            fontSize: Math.round(this._config.moneyFontSize * this._config.centsRelativeSize / 100)
        });
        this.addChild(this.centsText);

        this.currencyText = new Text(" " + (this._config.showSymbol ? this.symbol : BetModel.currency.ISO), {
            fontFamily: this._config.fontFamily,
            fontSize: Math.round(this._config.moneyFontSize * this._config.currencyRelativeSize / 100)
        });
        this.addChild(this.currencyText);
    }

    get bounds(): { width: number, height: number } {
        return {
            width: this._config.bounds.width,
            height: this._config.bounds.height
        };
    }

    set bounds(bounds: { width: number, height: number }) {
        this._config.bounds.width = bounds.width;
        this._config.bounds.height = bounds.height;
        if (this.boundsRect && this.boundsRect.parent) {
            this.boundsRect.parent.removeChild(this.boundsRect);
            this.boundsRect.destroy();
        }
        this.createBoundsRect();
        this.fitToBounds();
        this.positionText();
        // return {
        //     width: this._config.bounds.width,
        //     height: this._config.bounds.height
        // }
    }

    protected positionText() {
        const textWidth = this.textWidth;
        // Migration to PIXI v6 requires a cast
        const moneyAscent = (TextMetrics.measureText(this.moneyText.text, this.moneyText.style as TextStyle) as any).fontProperties.ascent;
        const centsAscent = (TextMetrics.measureText(this.centsText.text, this.centsText.style  as TextStyle) as any).fontProperties.ascent;
        const currencyAscent =(TextMetrics.measureText(this.currencyText.text, this.currencyText.style  as TextStyle) as any).fontProperties.ascent;

        const bounds = Utils.deepClone(this._config.bounds);
        if (this.coinBitmap) {
            bounds.width -= bounds.height * (this._config.coinsRelativeSize / 100);
        }

        if (bounds.height == 0) {
            this.moneyText.y = 0;
        } else {
            this.moneyText.y = (bounds.height - this.moneyText.height) / 2;
        }

        if (this._config.align == "left") {
            this.moneyText.x = 0;
        } else if (this._config.align == "right") {
            this.moneyText.x = bounds.width - textWidth;
        } else {
            this.moneyText.x = (bounds.width - textWidth) / 2;
            if (this._config.alignOnlyMoney && this.centsText.visible == false) {
                this.moneyText.x += this.currencyText.width / 2;
            }
        }

        if (this.centsText.visible) {
            this.centsText.x = this.moneyText.x + this.moneyText.width;
            this.centsText.y = this.moneyText.y + moneyAscent - centsAscent;
        }
        if (this._config.showIdentifier) {
            const visibleText = this.centsText.visible ? this.centsText : this.moneyText;
            this.currencyText.x = visibleText.x + visibleText.width;
            this.currencyText.y = this.moneyText.style.strokeThickness / 2 - this.currencyText.style.strokeThickness / 2 +
                this.moneyText.y + moneyAscent - currencyAscent;
        } else {
            this.currencyText.text = "";
            this.currencyText.visible = false;
        }

        if (this.coinBitmap) {
            let shift = bounds.height * this._config.coinsRelativeSize / 100;
            this.moneyText.x += shift;
            this.currencyText.x += shift;
            this.centsText.x += shift;
        }

        if (this.coinBitmap) {
            this.coinBitmap.x = this.moneyText.x - (this.coinBitmap.width / 1.1)
            this.coinBitmap.y = this.coinBitmap.y - 1
        }
    }

    private getFontSizes():TextFontSizes {
        let sizes = {
            money: this._config.moneyFontSize,
            cents: Math.round(this._config.moneyFontSize * this._config.centsRelativeSize / 100),
            currency: Math.round(this._config.moneyFontSize * this._config.currencyRelativeSize / 100)
        }

        if (this._config.shouldFitToBounds)
        {
            const coefCurrency: number = sizes.currency / sizes.money;
            const coefCents: number = sizes.cents / sizes.money;

            // Migration to PIXI v6 requires a cast
            let currencyStyle = (this.currencyText.style as TextStyle).clone();
            let moneyStyle = (this.moneyText.style as TextStyle).clone();
            let centsStyle = (this.centsText.style as TextStyle).clone();

            // Assume some big font size
            let moneyFontSize: number = 100;

            currencyStyle.fontSize = moneyFontSize * coefCurrency;
            moneyStyle.fontSize = moneyFontSize;
            centsStyle.fontSize = moneyFontSize * coefCents;
            const currencyMeasure = TextMetrics.measureText(this.currencyText.text, currencyStyle);
            const moneyMeasure = TextMetrics.measureText(this.moneyText.text, moneyStyle);
            const centsMeasure = TextMetrics.measureText(this.centsText.text, centsStyle);
            let measureWidth = 0;
            let measureHeight = 0;
            if (this.currencyText.visible) {
                measureWidth += currencyMeasure.width;
                measureHeight = Math.max(measureHeight, currencyMeasure.height);
            }

            if (this.moneyText.visible) {
                measureWidth += moneyMeasure.width;
                measureHeight = Math.max(measureHeight, moneyMeasure.height);
            }

            if (this.centsText.visible) {
                measureWidth += centsMeasure.width;
                measureHeight = Math.max(measureHeight, centsMeasure.height);
            }

            let width = this.bounds.width;
            if (this.coinBitmap) {
                // Ensure space for the bitmap coin
                width = this.bounds.width - this.bounds.height * (this._config.coinsRelativeSize / 100)
            }

            moneyFontSize *= Math.min(width / measureWidth, this.bounds.height / measureHeight);

            // Return the calculated sizes
            sizes = {
                money: Math.floor(moneyFontSize),
                cents: Math.floor(moneyFontSize * coefCents),
                currency: Math.floor(moneyFontSize * coefCurrency)
            }
        }

        return sizes;
    }

    private setFontSize(field: Text, value: number) {
        if (field.style.fontSize != value) {
            field.style.fontSize = value;
            let fontConfig = GameModel.gameConfig.fonts[field.style.fontFamily as string];
            if(fontConfig && fontConfig.padding) {
                field.style.padding =  Number(field.style.fontSize)*fontConfig.padding;
                // field.style.trim = field.text != " " && field.text != null && field.text != undefined && field.text != "";
            }

            if (fontConfig && fontConfig.fontWeight) {
                field.style.fontWeight = fontConfig.fontWeight;
            }

            if (fontConfig && fontConfig.textBaseline) {
                field.style.textBaseline = fontConfig.textBaseline;
            }
        }

    }

    protected fitToBounds() {
        let sizes =  this.getFontSizes();
        if (this.coinBitmap) {
            // Compute the bitmap coin size
            const bitmapCoinSize = this.bounds.height * (this._config.coinsRelativeSize / 100) / Layout.globalScale;
            this.coinBitmap.bound.width = bitmapCoinSize;
            this.coinBitmap.bound.height = bitmapCoinSize;
            // Center the bitmap coin vertically
            this.coinBitmap.bound.y = (this._config.bounds.height / Layout.globalScale - this.coinBitmap.bound.height) / 2;

            this.coinBitmap.applyBound();
        }

        this.setFontSize(this.moneyText, sizes.money);
        this.setFontSize(this.centsText, sizes.cents);
        this.setFontSize(this.currencyText, sizes.currency);
    }

    set value(value: number) {
        if (!this.useCoins) {
            if (this._config.bounds.id == UIComponents.BalanceAlternative) {
                // We need to truncate the alternative balance
                // Balance 0.597 must be displaed as 0.59 (not 0.60)
                value = Utils.truncateNumber(value * BetModel.currencyRate, BetModel.currency.decimals);
            } else {
                value *= BetModel.currencyRate;
            }
        }

        this.money = value;
        this.moneyText.text = this.moneyString;
        this.centsText.text = this.centsString;
        // this.moneyText.style.trim = this.moneyText.text && this.moneyText.text != " " && this.moneyText.text != "";
        // this.centsText.style.trim = this.centsText.text && this.centsText.text != " " && this.centsText.text != "";
        // this.currencyText.style.trim = this.currencyText.text && this.currencyText.text != " " && this.currencyText.text != "";
        if (this._config.hideWhenZero && value == 0) {
            this.moneyText.visible = false;
            this.centsText.visible = false;
            this.currencyText.visible = false;
        } else {
            this.moneyText.visible = true;
            this.centsText.visible = this.shouldCentsHaveValue();
        }

        this.fitToBounds();
        this.positionText();
    }

    get value() {
        return this.money;
    }


    get style() {
        return this.moneyText.style;
    }

    set style(style: ITextFieldStyleOptions) {
        this.overrideStyle = style;
        const moneyStyle = this.moneyText.style;
        const centsStyle = this.centsText.style;
        const currencyStyle = this.currencyText.style;
        for (const key in style) {
            if (key != "fontSize" && key != "font") {
                moneyStyle[key] = style[key];
                centsStyle[key] = style[key];
                currencyStyle[key] = style[key];
            }
        }

        this.moneyText.style = moneyStyle;
        this.centsText.style = centsStyle;
        this.currencyText.style = currencyStyle;


        this.fitToBounds();
        this.positionText();
    }

    set moneyStyle(style: ITextFieldStyleOptions) {
        this.applyStyle(this.moneyText, style);
    }

    set centsStyle(style: ITextFieldStyleOptions) {
        this.applyStyle(this.centsText, style);
    }

    set currencyStyle(style: any) {
        this.applyStyle(this.currencyText, style);
    }

    private applyStyle(textField, style: ITextFieldStyleOptions) {
        const textStyle = textField.style;
        for (const key in style) {
            if (key != "fontSize" && key != "font") {
                textStyle[key] = style[key];
            }
        }
        textField.style = textStyle;
        this.fitToBounds();
        this.positionText();
    }

    // set currencyText // TODO

    get textWidth() {
        // const moneyWidth = this.moneyText.context.measureText(this.moneyText.text).width;
        // const centsWidth = this.centsText.visible ? this.centsText.context.measureText(this.centsText.text).width : 0;
        // const currencyWidth = this.currencyText.context.measureText(this.currencyText.text).width;
        const moneyWidth = this.moneyText.width;
        const centsWidth = this.centsText.visible ? this.centsText.width : 0;
        const currencyWidth = this.currencyText.visible ? this.currencyText.width : 0;
        return moneyWidth + centsWidth + currencyWidth;
    }

    get textHeight() {
        return Math.max(
            this.moneyText.height,
            this.centsText.height,
            !this._config.keepCurrencySize && this.currencyText.height || 0
        );
    }

    get moneyString() {
        let result;
        if (BetModel.isDisplayingCredits) {
            // if (false) {
            result = Math.floor(this.money * BetModel.denomination);
        } else {
            result = (this.money / App.config.subUnits).toFixed(
                BetModel.currency.decimals
            );
            const moneyParts = result.split(".");
            const digits = moneyParts[0].split('');

            if (this._config.spaceBetweenThousands) {
                let counter = 3;
                let digitIndex = digits.length;
                while (--digitIndex > 0) {
                    counter--;
                    if (counter == 0) {
                        counter = 3;
                        digits.splice(digitIndex, 0, this._config.symbolBetweenThousands);
                    }
                }
            }

            result = digits.join('') + (this.shouldCentsHaveValue() ? this._config.decimalSeparatorSymbol : "");
        }

        if (this._config.showIdentifier) {
            if (this._config.prependSymbol) {
                result = (this._config.showSymbol ? this.symbol : BetModel.currency.ISO) + " " + result;
                this.currencyText.visible = false;
            }
        }

        return result;
    }

    get centsString() {
        let result;
        if (this.shouldCentsHaveValue()) {
            result = (this.money / App.config.subUnits)
                .toFixed(BetModel.currency.decimals)
                .split(".")[1];
        }
        result = result
        ? result
        : 1;

        return result;
    }

    private shouldCentsHaveValue(): boolean {
        // Destructure the relevant configuration for easier access
        const config = Configs?.components?.moneyFields;
    
        // Check if all bitmap money fields should hide cents
        const hideAll = config?.hideAllBitmapMoneyFields;
    
        // Check if cents should be hidden for this specific money field and the value is an integer
        const hideCentsForField = config?.hideCentsForMoneyFields?.includes(this._config.bounds.id) && isInteger(this.value);
    
        // Check if there are specific conditions to hide cents for this field
        const hideCentsCondition = config?.hideCentsConditions?.fields.some(field => this._config.bounds.id.includes(field)) &&
                                   isInteger(this.value) &&
                                   config?.hideCentsConditions?.condition(this);
        

        // If any of the conditions to hide cents are met, return false
        if (hideAll || hideCentsForField || hideCentsCondition) {
            return false;
        }
    
        // Default case: return true if not displaying credits and currency supports decimals
        return !BetModel.isDisplayingCredits && BetModel.currency.decimals > 0;
    };

    public  rescale(bounds:IObjectConfig){
        Layout.setY(this, bounds.y);
        Layout.setX(this, bounds.x);
        Layout.testSetWidth(this, bounds.width);
    }

}
